// react
import React, { useState, useMemo } from "react";

// prop-types
import PropTypes from "prop-types";

// react-i18next
import { useTranslation } from "react-i18next";

// context
import { UserAuth } from "context/AuthContext";

// @mui
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableFooter,
  TablePagination,
  Button,
  IconButton,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon,
  KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon,
  Add as AddIcon,
  DeleteForever as DeleteForeverIcon,
} from "@mui/icons-material";

// components
import { MemberRemoveConfirm } from "components/app/AppFeedback";

// features/group
import { removeMember } from "features/group/manageMember";

// A ---------------------------------------------------------------------- M

const MembersTableToolbar = ({ creator_uuid, setOpen }) => {
  const { user } = UserAuth();
  const { t } = useTranslation();

  return (
    <Grid container alignItems="center" spacing={2} mb={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="body1" fontWeight={700} color="text.secondary">
          {t("members")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} container justifyContent="flex-end" spacing={1}>
        {user.uid === creator_uuid && (
          <Grid item sm="auto">
            <Button
              onClick={() => setOpen(true)}
              startIcon={<AddIcon />}
              variant="contained"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
            >
              {t("member")}
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const MembersTableHead = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const headCells = useMemo(
    () => [
      { id: "email", label: t("email") },
      { id: "role", label: t("role") },
      { id: "actions", label: t("actions") },
    ],
    [t]
  );

  return (
    <TableHead sx={{ background: `linear-gradient(90deg, ${theme.palette.primary.main}33, ${theme.palette.primary.dark}11)` }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align={headCell.id !== "actions" ? "left" : "right"} sx={{ fontWeight: 700 }}>
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {isRtl ? <KeyboardArrowRightOutlinedIcon /> : <KeyboardArrowLeftOutlinedIcon />}
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {isRtl ? <KeyboardArrowLeftOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const MembersTable = ({ group, members, setOpen, handleSuccessfulRemoved, handleErrorRemoved }) => {
  const { user } = UserAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedMember, setSelectedMember] = useState();
  const [selectedGroup, setSelectedGroup] = useState();
  const [showRemove, setShowRemove] = useState(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleShowRemove = (memberUID, groupID) => {
    setShowRemove(true);
    setSelectedMember(memberUID);
    setSelectedGroup(groupID);
  };

  const handleRemoveMember = async () => {
    try {
      await removeMember(selectedMember, selectedGroup);
      setShowRemove(false);
      handleSuccessfulRemoved();
    } catch (error) {
      handleErrorRemoved();
    }
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - members.length) : 0;

  return (
    <>
      <Box>
        <MembersTableToolbar creator_uuid={group.creator_uuid} setOpen={setOpen} />

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}CC)`,
            backdropFilter: "blur(6px)",
            border: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
          }}
        >
          <Table>
            {!isMobile && <MembersTableHead />}

            <TableBody>
              {(rowsPerPage > 0 ? members.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : members).map((member) => {
                const isAdmin = member.uid === group.creator_uuid;
                const isRemovable = user.uid === group.creator_uuid && member.uid !== group.creator_uuid;

                return isMobile ? (
                  <Box
                    key={member.uid}
                    sx={{
                      p: 2,
                      mb: 2,
                      mx: 1,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                      cursor: "pointer",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.01)",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                      },
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      {member.email || "N/A"}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {isAdmin ? t("admin") : t("member")}
                    </Typography>

                    {isRemovable && (
                      <Box mt={2} display="flex" justifyContent="flex-end">
                        <Tooltip title={t("remove")} placement="top">
                          <IconButton color="error" onClick={() => handleShowRemove(member.uid, group.id)}>
                            <DeleteForeverIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <TableRow key={member.uid}>
                    <TableCell>{member.email || "N/A"}</TableCell>

                    <TableCell sx={{ color: "gray" }}>{isAdmin ? t("admin") : t("member")}</TableCell>

                    <TableCell align="right">
                      {isRemovable && (
                        <Tooltip title={t("remove")} placement="top">
                          <IconButton color="error" onClick={() => handleShowRemove(member.uid, group.id)}>
                            <DeleteForeverIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!isMobile && emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={3} />
                </TableRow>
              )}
            </TableBody>

            {!isMobile && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 20]}
                    count={members.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t("rows_per_page")}
                    labelDisplayedRows={({ from, to, count }) => t("displayed_rows", { from, to, count: count !== -1 ? count : `more than ${to}` })}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>

          {isMobile && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <TablePaginationActions count={members.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
            </Box>
          )}
        </TableContainer>
      </Box>

      <MemberRemoveConfirm showRemove={showRemove} setShowRemove={setShowRemove} handleRemoveMember={handleRemoveMember} />
    </>
  );
};

MembersTable.propTypes = {
  group: PropTypes.object.isRequired,
  members: PropTypes.array.isRequired,
  setOpen: PropTypes.func.isRequired,
  handleSuccessfulRemoved: PropTypes.func.isRequired,
  handleErrorRemoved: PropTypes.func.isRequired,
};

export default MembersTable;
